/**
 * Campaign tags: caught on arrival, filled into forms later.
 *
 * A visitor lands on one page with utm_source in the address, reads three more
 * pages, and only then writes to you. By that point the address is clean — so
 * the tags are taken from the URL on the first page that carries them and kept
 * for the visit; a form filled in later gets what brought the person here.
 *
 * The builder writes the fields as hidden inputs marked `gf-track-<tag>`
 * (inc/form-builder.php). Anything not built there can carry the same class by
 * hand and be filled the same way.
 *
 * Nothing here reaches a third party and nothing is written unless a tag was
 * actually present: no tags in the address, no storage touched.
 */

const KEY = 'gf-marks';
const PREFIX = 'gf-track-';

/** What an ad platform leaves in the address. */
const FROM_URL = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
];

type Marks = Record<string, string>;

const load = (): Marks => {
  try {
    return JSON.parse(window.sessionStorage.getItem(KEY) || '{}') as Marks;
  } catch {
    return {};
  }
};

const save = (marks: Marks): void => {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(marks));
  } catch {
    // Private window, storage blocked: the fields simply stay empty.
  }
};

/** The Google Analytics client id, as its own cookie spells it. */
const clientId = (): string => {
  const match = document.cookie.match(/_ga=GA\d\.\d\.(\d+\.\d+)/);

  return match ? match[1] : '';
};

/**
 * Take whatever this page's address carries and remember it.
 *
 * First touch wins: someone who arrives from an ad and later comes back through
 * a search should still be counted to the ad that started it. The page and the
 * referrer are recorded once, for the same reason.
 */
export const captureMarks = (): Marks => {
  const marks = load();
  const params = new URLSearchParams(window.location.search);
  let changed = false;

  FROM_URL.forEach((key) => {
    const value = params.get(key);

    if (value && !marks[key]) {
      marks[key] = value.slice(0, 200);
      changed = true;
    }
  });

  if (changed || Object.keys(marks).length) {
    if (!marks.landing) {
      marks.landing = window.location.href.split('#')[0].slice(0, 300);
      changed = true;
    }

    // Only a referrer from somewhere else says anything.
    if (!marks.referrer && document.referrer && !document.referrer.startsWith(window.location.origin)) {
      marks.referrer = document.referrer.slice(0, 300);
      changed = true;
    }
  }

  if (changed) {
    save(marks);
  }

  return marks;
};

/**
 * Fill the marked inputs of every form in scope.
 *
 * A field the plugin already filled — default:get, when the tag is in this
 * page's address — is left alone: the address is the better answer.
 */
export const fillMarks = (scope: ParentNode = document): void => {
  const inputs = scope.querySelectorAll<HTMLInputElement>(`input[class*="${PREFIX}"]`);

  if (!inputs.length) {
    return;
  }

  const marks = captureMarks();

  inputs.forEach((input) => {
    if (input.value.trim()) {
      return;
    }

    const found = Array.from(input.classList).find((name) => name.startsWith(PREFIX));
    const source = found ? found.slice(PREFIX.length) : '';

    if (!source) {
      return;
    }

    if ('page' === source) {
      input.value = window.location.href.split('#')[0];

      return;
    }

    if ('ga_client_id' === source) {
      input.value = clientId();

      return;
    }

    input.value = marks[source] || '';
  });
};

export default fillMarks;
